/**
 * API Client Service
 * Handles HTTP requests to the backend API
 */

import * as Sentry from '@sentry/react';
import { getCurrentUserToken } from './auth';
import { readPlanBoard } from '../features/player-database/planning/planBoardMemory';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5206';
const ACTIVE_ORG_STORAGE_KEY = 'activeOrganizationId';

/**
 * Get the active organization ID for system admin org switching
 */
const getOrganizationOverrideId = (): string | null => {
  return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
};

/**
 * Phase 1b.5: the active view id the client sends on every authenticated request as
 * `X-Active-View`. Held at module scope so api.ts does not need to reach into Firebase or
 * localStorage on every call; AuthContext owns the writer and keeps it in sync with the
 * signed-in user's choice.
 */
let activeViewId: string | null = null;

/** AuthContext calls this on sign-in, view switch, and sign-out (passing null). */
export const setActiveViewId = (id: string | null): void => {
  activeViewId = id;
};

/** For testing / rare direct reads. The request path uses the module variable directly. */
export const getActiveViewId = (): string | null => activeViewId;

/** The header that carries a system admin's Baseball / Softball switch to the directory reads. */
export const DIRECTORY_SPORT_HEADER = 'X-Directory-Sport';
const DIRECTORY_PATH = '/api/directory/';

/**
 * The sport a system admin picked for the Recruiting Directory, sent as `X-Directory-Sport` on every
 * `/api/directory/` request. `DirectorySportProvider` owns the writer and leaves it null for everyone
 * else; the API ignores the header for any caller who is not a system admin either way.
 */
let directorySport: string | null = null;

export const setDirectorySportHeader = (sport: string | null): void => {
  directorySport = sport;
};

export const getDirectorySportHeader = (): string | null => directorySport;

/** The header that names the coach's needs board, which Worth to you is weighed by. */
export const NEEDS_BOARD_HEADER = 'X-Needs-Board';

/**
 * The signed-in user whose remembered needs board (`planBoardMemory`) rides on every `/api/directory/`
 * request as `X-Needs-Board`. `DirectorySportProvider` owns the writer. The board itself is read from
 * storage per request, so it always matches what the "Your needs" card shows. The API checks the id
 * belongs to the caller's program and falls back to the program's first recruiting board otherwise.
 */
let needsBoardUserId: string | null = null;

/** `/api/home` itself or anything under it, with or without a query; not `/api/homework`. */
const isHomeEndpoint = (endpoint: string): boolean => /^\/api\/home(?:[/?]|$)/.test(endpoint);

export const setNeedsBoardUser = (userId: string | null): void => {
  needsBoardUserId = userId;
};

export class ApiError extends Error {
  statusCode: number;
  response?: unknown;

  constructor(message: string, statusCode: number, response?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.response = response;
  }
}

export interface ApiRequestOptions extends RequestInit {
  timeout?: number;
  /** `response` hands back the raw `Response` for a caller that reads a streamed body itself. */
  responseType?: 'json' | 'blob' | 'response';
}

/**
 * The error code the API returns (with a 403) for every request made by a user
 * whose organization has been paused.
 */
const ORGANIZATION_PAUSED_CODE = 'organization_paused';

/**
 * The error code the API returns (with a 403) for every non-allowlisted request
 * made by a user whose self-serve organization has not paid yet. Its own code,
 * not the pause: the remedy is "complete payment", not "contact support".
 */
const ORGANIZATION_AWAITING_PAYMENT_CODE = 'organization_awaiting_payment';

/**
 * Notified with the server-provided pause message (or null if the server sent
 * none) whenever a request is rejected because the organization is paused.
 */
export type OrganizationPausedHandler = (message: string | null) => void;

/**
 * Notified with the server's copy (or null) whenever a request is rejected
 * because the organization is awaiting its first payment.
 */
export type OrganizationAwaitingPaymentHandler = (message: string | null) => void;

const organizationPausedHandlers = new Set<OrganizationPausedHandler>();
const organizationAwaitingPaymentHandlers = new Set<OrganizationAwaitingPaymentHandler>();

/**
 * Subscribe to mid-session organization pauses. Returns an unsubscribe function.
 *
 * This lets AuthContext react to an organization being paused while the user is
 * already logged in (their cached profile still says "not paused").
 */
export const subscribeToOrganizationPaused = (
  handler: OrganizationPausedHandler
): (() => void) => {
  organizationPausedHandlers.add(handler);
  return () => {
    organizationPausedHandlers.delete(handler);
  };
};

/**
 * Subscribe to "organization awaiting payment" refusals. Returns an unsubscribe
 * function. AuthContext uses it to route the user to /signup/payment instead of
 * leaving them in an app where every request fails.
 */
export const subscribeToOrganizationAwaitingPayment = (
  handler: OrganizationAwaitingPaymentHandler
): (() => void) => {
  organizationAwaitingPaymentHandlers.add(handler);
  return () => {
    organizationAwaitingPaymentHandlers.delete(handler);
  };
};

/**
 * The error code the API returns (with a 403) for every non-allowlisted request made by a user who
 * has not accepted the current Terms of Service or Privacy Policy. The body carries
 * `legalAcceptanceRequired`, the documents to show.
 */
const LEGAL_ACCEPTANCE_REQUIRED_CODE = 'legal_acceptance_required';

/**
 * Notified with the raw `legalAcceptanceRequired` from the 403 body (unparsed: the subscriber reads
 * it through `parseLegalAcceptanceRequired`) whenever a request is refused for pending legal
 * acceptance.
 */
export type LegalAcceptanceRequiredHandler = (legalAcceptanceRequired: unknown) => void;

const legalAcceptanceRequiredHandlers = new Set<LegalAcceptanceRequiredHandler>();

/**
 * Subscribe to "legal acceptance required" refusals. Returns an unsubscribe function. AuthContext
 * uses it to put up the acceptance dialog when the documents changed mid-session.
 */
export const subscribeToLegalAcceptanceRequired = (
  handler: LegalAcceptanceRequiredHandler
): (() => void) => {
  legalAcceptanceRequiredHandlers.add(handler);
  return () => {
    legalAcceptanceRequiredHandlers.delete(handler);
  };
};

type BlockingState = 'paused' | 'awaitingPayment' | 'legalAcceptance';

const BLOCKING_BREADCRUMBS: Record<BlockingState, string> = {
  paused: '403 organization_paused - routing to blocking screen',
  awaitingPayment: '403 organization_awaiting_payment - routing to payment screen',
  legalAcceptance: '403 legal_acceptance_required - showing the acceptance dialog',
};

/**
 * Notify subscribers if this failed response is one of the blocking 403s (organization paused,
 * awaiting payment, or legal acceptance pending). Never throws - the caller still throws its
 * own ApiError afterwards.
 *
 * Returns which state it was, so the caller can log it as the known, handled
 * state it is rather than as an unexpected API error.
 */
const notifyIfOrganizationBlocked = (statusCode: number, body: unknown): BlockingState | null => {
  if (statusCode !== 403 || typeof body !== 'object' || body === null) {
    return null;
  }

  const { code, message, legalAcceptanceRequired } = body as {
    code?: unknown;
    message?: unknown;
    legalAcceptanceRequired?: unknown;
  };

  if (code === LEGAL_ACCEPTANCE_REQUIRED_CODE) {
    legalAcceptanceRequiredHandlers.forEach((handler) => {
      try {
        handler(legalAcceptanceRequired);
      } catch (handlerError) {
        console.error('Legal acceptance handler failed:', handlerError);
      }
    });
    return 'legalAcceptance';
  }

  let state: BlockingState;
  let handlers: Set<(message: string | null) => void>;

  if (code === ORGANIZATION_PAUSED_CODE) {
    state = 'paused';
    handlers = organizationPausedHandlers;
  } else if (code === ORGANIZATION_AWAITING_PAYMENT_CODE) {
    state = 'awaitingPayment';
    handlers = organizationAwaitingPaymentHandlers;
  } else {
    return null;
  }

  const serverMessage = typeof message === 'string' && message.trim() ? message : null;

  handlers.forEach((handler) => {
    try {
      handler(serverMessage);
    } catch (handlerError) {
      console.error(`Organization ${state} handler failed:`, handlerError);
    }
  });

  return state;
};

/**
 * Make an authenticated API request
 */
export const apiRequest = async <T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  // Get fresh token from Firebase (automatically refreshes if expired)
  const token = await getCurrentUserToken();
  const { timeout, responseType = 'json', ...fetchOptions } = options;

  // Safely extract custom headers
  let customHeaders: Record<string, string> = {};
  if (options.headers) {
    if (options.headers instanceof Headers) {
      // Convert Headers object to plain object
      options.headers.forEach((value, key) => {
        customHeaders[key] = value;
      });
    } else if (Array.isArray(options.headers)) {
      // Convert array of tuples to object
      options.headers.forEach(([key, value]) => {
        customHeaders[key] = value;
      });
    } else if (typeof options.headers === 'object') {
      // Already a plain object
      customHeaders = options.headers as Record<string, string>;
    }
  }

  // A FormData body must not carry a Content-Type of ours: the browser writes
  // `multipart/form-data` with the boundary itself, and a hand-set one has none.
  const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...customHeaders,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add organization override header for system admin org switching
  const orgOverrideId = getOrganizationOverrideId();
  if (orgOverrideId) {
    headers['X-Organization-Id'] = orgOverrideId;
  }

  // Phase 1b.5: tell the server which workspace this request is for. Absent header = the
  // server picks the user's default view (college when present, else first TB view). Only
  // attached when a caller has NOT explicitly set it in `customHeaders`, so a one-off override
  // (a test or a cross-view read) wins.
  if (activeViewId && !('X-Active-View' in headers)) {
    headers['X-Active-View'] = activeViewId;
  }

  if (directorySport && endpoint.startsWith(DIRECTORY_PATH) && !(DIRECTORY_SPORT_HEADER in headers)) {
    headers[DIRECTORY_SPORT_HEADER] = directorySport;
  }

  // The Home page (`/api/home`) reads portal players for the same needs board, so it carries the
  // header too; otherwise its portal card could name a different board than the Player Database.
  if (
    (endpoint.startsWith(DIRECTORY_PATH) || isHomeEndpoint(endpoint)) &&
    !(NEEDS_BOARD_HEADER in headers)
  ) {
    const needsBoard = readPlanBoard(needsBoardUserId);
    if (needsBoard) headers[NEEDS_BOARD_HEADER] = needsBoard;
  }

  const url = `${API_BASE_URL}${endpoint}`;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;

  // If caller provided their own signal (e.g. for cancellation), link it to our controller
  const callerSignal = fetchOptions.signal;
  if (callerSignal) {
    delete fetchOptions.signal;
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });

    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
      let errorData: unknown;

      try {
        errorData = await response.json();
        const parsedMessage = (errorData as { message?: unknown }).message;
        if (typeof parsedMessage === 'string' && parsedMessage) {
          errorMessage = parsedMessage;
        }
      } catch {
        // Response is not JSON, use default message
      }

      // Let subscribers (AuthContext) know the org was paused, or is awaiting
      // payment. Additive only - the ApiError below is still thrown as before.
      const blockingState = notifyIfOrganizationBlocked(response.status, errorData);

      const method = fetchOptions.method || 'GET';
      // Query strings are stripped before anything is reported: they carry
      // search terms, which are PII in this app.
      const path = endpoint.split('?')[0];

      if (blockingState) {
        // A paused or unpaid org, or pending legal acceptance, is a known, handled
        // state, and between the refusal and the blocking screen *every* in-flight
        // request 403s - one concise breadcrumb each instead of a full error context
        // with the body.
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'info',
          message: BLOCKING_BREADCRUMBS[blockingState],
          data: { path, method },
        });
      } else {
        // Add Sentry context for API errors
        Sentry.setContext('api_request', {
          endpoint: path,
          method,
          status_code: response.status,
          error_data: errorData,
        });
      }

      throw new ApiError(errorMessage, response.status, errorData);
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return {} as T;
    }

    // Parse response based on type
    if (responseType === 'response') {
      return response as T;
    }

    if (responseType === 'blob') {
      return await response.blob() as T;
    }

    return await response.json();
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);

    // Handle timeout
    if (error instanceof Error && error.name === 'AbortError') {
      Sentry.addBreadcrumb({
        category: 'api_timeout',
        message: `Request aborted: ${fetchOptions.method || 'GET'} ${endpoint}`,
        level: 'warning',
        data: {
          endpoint,
          method: fetchOptions.method || 'GET',
          had_timeout: !!timeout,
          timeout_ms: timeout,
        },
      });
      throw new ApiError('Request timeout', 408);
    }

    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    throw new ApiError(
      error instanceof Error ? error.message : 'Network request failed',
      0
    );
  }
};

/**
 * GET request
 */
export const get = <T>(endpoint: string, options?: ApiRequestOptions): Promise<T> => {
  return apiRequest<T>(endpoint, { ...options, method: 'GET' });
};

/**
 * POST request
 */
export const post = <T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
};

/**
 * POST a multipart form (file uploads). The browser sets the Content-Type and boundary.
 */
export const postFormData = <T>(
  endpoint: string,
  formData: FormData,
  options?: ApiRequestOptions
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: formData,
  });
};

/**
 * PUT request
 */
export const put = <T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
};

/**
 * PATCH request
 */
export const patch = <T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
};

/**
 * DELETE request
 */
export const del = <T>(endpoint: string, options?: ApiRequestOptions): Promise<T> => {
  return apiRequest<T>(endpoint, { ...options, method: 'DELETE' });
};

/**
 * GET request that returns a blob (for file downloads)
 */
export const getBlob = (endpoint: string, options?: ApiRequestOptions): Promise<Blob> => {
  return apiRequest<Blob>(endpoint, {
    ...options,
    method: 'GET',
    responseType: 'blob'
  });
};
